/**
 * Mélange DÉTERMINISTE, semé par l'identifiant de la partie.
 *
 * Sans mélange, chaque expédition présentait ses vignettes dans le même ordre :
 * un jeune qui rejoue — ou qui regarde par-dessus l'épaule d'un autre — retient
 * les positions plutôt que les objets. C'est précisément ce que les leurres
 * cherchent à empêcher.
 *
 * POURQUOI PAS `Math.random()` : ce serait un défaut, pas une fonctionnalité.
 *
 *  - React réaffiche à chaque coche. Un tirage à chaque rendu réorganiserait la
 *    grille sous les doigts du jeune, au moment même où il coche.
 *  - Sur un téléphone, le navigateur recharge la page tout seul : écran
 *    verrouillé, onglet en veille, réseau qui saute. Un jeune ayant parcouru la
 *    moitié du cosmodôme retrouverait une grille entièrement redistribuée et
 *    perdrait ses repères en plein jeu.
 *
 * D'où une graine : l'identifiant de la partie. MÊME PARTIE → MÊME ORDRE,
 * rechargements compris. NOUVELLE PARTIE → NOUVEL ORDRE.
 *
 * L'ordre se calcule par élément, à partir de `graine + clé de l'élément`, et
 * non par un brassage du tableau. Conséquence utile : ajouter ou retirer un
 * objet ne redistribue pas tous les autres, et l'ordre ne dépend pas de celui
 * dans lequel le serveur a répondu.
 */

/**
 * Empreinte 32 bits d'une chaîne (FNV-1a).
 *
 * Ce n'est PAS une fonction de hachage cryptographique et elle n'a pas à
 * l'être : elle ne protège rien, elle répartit. La seule propriété attendue est
 * que deux chaînes proches — deux UUID qui ne diffèrent que d'un caractère —
 * donnent des valeurs éloignées.
 */
function empreinte(texte: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < texte.length; i++) {
    h ^= texte.charCodeAt(i);
    // Multiplication FNV, en restant dans les entiers 32 bits.
    h = Math.imul(h, 0x01000193);
  }

  // BRASSAGE FINAL, et il n'est pas décoratif. Sans lui, FNV seul donnait une
  // répartition franchement mauvaise sur nos données : les identifiants ne
  // diffèrent que par leurs derniers caractères (« objet-01 » … « objet-20 »),
  // qui ne subissent alors qu'une seule multiplication et pèsent peu sur les
  // bits de poids fort — ceux qui décident du tri. Mesuré : sur 2000 parties,
  // un même objet arrivait premier 676 fois au lieu d'une centaine, et trois
  // parties tirées au hasard commençaient toutes par le même.
  //
  // Ces trois tours (finaliseur de MurmurHash3) dispersent les bits de poids
  // faible vers les forts et règlent le problème.
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;

  // `>>> 0` ramène dans les entiers positifs : un ordre de tri sur des valeurs
  // signées enverrait la moitié des éléments devant, sans raison.
  return h >>> 0;
}

/**
 * Renvoie une COPIE de la liste, dans un ordre stable propre à cette graine.
 *
 * `cleDe` doit donner une valeur stable et unique par élément — l'identifiant
 * en base, jamais l'indice dans le tableau.
 */
export function ordreSeme<T>(elements: readonly T[], graine: string, cleDe: (element: T) => string): T[] {
  return [...elements].sort((a, b) => {
    // La CLÉ d'abord, la graine ensuite : c'est la clé qui varie d'un élément à
    // l'autre, elle traverse ainsi toute la boucle de hachage plutôt que ses
    // dernières itérations seulement.
    const ea = empreinte(`${cleDe(a)}:${graine}`);
    const eb = empreinte(`${cleDe(b)}:${graine}`);
    // Égalité d'empreinte : très improbable, mais le tri doit rester total et
    // reproductible. On départage sur la clé, qui est unique.
    return ea === eb ? cleDe(a).localeCompare(cleDe(b)) : ea - eb;
  });
}
